import React, { useEffect, useMemo } from 'react';
import {
  AccountBalance,
  CheckCircle,
  Description,
  LocationOn,
  Person,
  Phone,
  Schedule,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';

import { useGetContractQuery } from '@/apis/extended/contractApi';
import { useEnsureClosingTasksMutation } from '@/apis/extended/pipelineApi';
import {
  useGetContractTasksQuery,
  useUpdateContractTaskMutation,
} from '@/apis/extended/tasksApi';
import GlassButton from '@/components/ui/GlassButton';
import GlassCard from '@/components/ui/GlassCard';
import { calculateContractTimeline } from '@/utils/contractTimeline';

interface ClosingCoordinationProps {
  contractId: string;
  userRole: 'buyer' | 'seller' | 'agent';
}

const ClosingCoordination: React.FC<ClosingCoordinationProps> = ({
  contractId,
  userRole,
}) => {
  const { data: contract, isLoading: contractLoading } =
    useGetContractQuery(contractId);
  const { data: tasksData } = useGetContractTasksQuery(contractId, {
    skip: !contractId,
  });
  const [ensureClosingTasks] = useEnsureClosingTasksMutation();
  const [updateTask, { isLoading: isUpdatingTask }] =
    useUpdateContractTaskMutation();

  useEffect(() => {
    if (!contractId) return;
    // Create any missing closing tasks deterministically (idempotent endpoint).
    ensureClosingTasks(contractId).catch((e) => console.error(e));
  }, [contractId, ensureClosingTasks]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const getAssignedToIcon = (assignedTo: string) => {
    switch (assignedTo) {
      case 'buyer':
      case 'seller':
        return <Person />;
      case 'agent':
        return <Person />;
      default:
        return <Person />;
    }
  };

  const timelineSteps = useMemo(() => {
    if (!contract) return [];
    const events = calculateContractTimeline(contract);
    // Focus the coordinator view on the last-mile milestones.
    const ids = new Set(['title', 'financing', 'closing']);
    const filtered = events.filter((e) => ids.has(e.id));
    const ratified = contract.ratifiedAt
      ? {
          id: 'ratified',
          title: 'Contract Ratified',
          description: 'Contract has been ratified by all parties.',
          dueDate: contract.ratifiedAt,
          status: 'completed' as const,
        }
      : null;
    return [ratified, ...filtered].filter(Boolean) as any[];
  }, [contract]);

  const closingTasks = useMemo(() => {
    const raw = (tasksData?.data || []) as any[];
    return raw.filter((t) =>
      String(t?.related?.milestoneId || '').startsWith('closing_')
    );
  }, [tasksData]);

  const activeStep = useMemo(() => {
    // Advance based on the first incomplete closing task, otherwise final step.
    const idx = closingTasks.findIndex((t) => t.status !== 'completed');
    if (idx === -1) return Math.max(0, timelineSteps.length - 1);
    return Math.min(idx + 1, Math.max(0, timelineSteps.length - 1));
  }, [closingTasks, timelineSteps.length]);

  const completedTasks = closingTasks.filter(
    (t) => t.status === 'completed'
  ).length;
  const totalTasks = closingTasks.length || 1;
  const progressPercentage = (completedTasks / totalTasks) * 100;

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Closing Coordination
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Track closing progress and coordinate with all parties. Closing date is{' '}
        {contract?.offerSpecifics?.closingDate
          ? new Date(contract.offerSpecifics.closingDate).toLocaleDateString()
          : 'TBD'}
        .
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <GlassCard variant="medium">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Closing Progress
              </Typography>
              <Stepper activeStep={activeStep} orientation="vertical">
                {timelineSteps.map((step: any) => (
                  <Step key={step.id}>
                    <StepLabel>{step.title}</StepLabel>
                    <StepContent>
                      <Typography variant="body2" color="text.secondary">
                        {step.description || ''}
                      </Typography>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </GlassCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <GlassCard variant="subtle">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Closing Details
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Schedule />
                  </ListItemIcon>
                  <ListItemText
                    primary="Closing Date"
                    secondary={
                      contract?.offerSpecifics?.closingDate
                        ? new Date(
                            contract.offerSpecifics.closingDate
                          ).toLocaleDateString()
                        : 'TBD'
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Schedule />
                  </ListItemIcon>
                  <ListItemText primary="Closing Time" secondary="2:00 PM" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <LocationOn />
                  </ListItemIcon>
                  <ListItemText
                    primary="Location"
                    secondary={
                      contract?.thirdPartyTitleSettlement?.titleCompany || 'TBD'
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Phone />
                  </ListItemIcon>
                  <ListItemText primary="Title Contact" secondary="(TBD)" />
                </ListItem>
              </List>
            </CardContent>
          </GlassCard>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Task Progress
              </Typography>
              <Typography variant="h4" color="primary">
                {Math.round(progressPercentage)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {completedTasks} of {totalTasks} tasks completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <GlassCard variant="medium" sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Closing Tasks
          </Typography>
          <List>
            {closingTasks.map((task: any) => (
              <ListItem key={task._id} divider>
                <Checkbox
                  checked={task.status === 'completed'}
                  onChange={(e) =>
                    updateTask({
                      taskId: task._id,
                      updateData: {
                        status: e.target.checked ? 'completed' : 'pending',
                      },
                    }).catch((err) => console.error(err))
                  }
                  disabled={
                    (userRole !== task.assignedToRole &&
                      userRole !== 'agent') ||
                    isUpdatingTask
                  }
                />
                <ListItemIcon>
                  {getAssignedToIcon(task.assignedToRole)}
                </ListItemIcon>
                <ListItemText
                  disableTypography
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <span>{task.title}</span>
                      <Chip
                        label={task.priority}
                        size="small"
                        color={getPriorityColor(task.priority) as any}
                      />
                      <Chip
                        label={String(task.assignedToRole).replace('_', ' ')}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {task.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Due:{' '}
                        {task.dueDate
                          ? new Date(task.dueDate).toLocaleDateString()
                          : 'TBD'}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </GlassCard>
    </Box>
  );
};

export default ClosingCoordination;
